import React from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Shield, Lock, Eye, AlertTriangle, CheckCircle2, Users, BadgeCheck, Scale } from 'lucide-react';

const TrustPage = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-foreground mb-4">Trust & Safety</h1>
            <p className="text-xl text-muted-foreground">Building a safer crowdfunding experience</p>
          </div>

          <div className="space-y-8">
            <section className="bg-card border border-border rounded-xl p-8">
              <div className="flex items-center gap-4 mb-4">
                <Lock className="w-8 h-8 text-emerald-500" />
                <h2 className="text-xl font-semibold text-foreground">Secure Payments</h2>
              </div>
              <p className="text-muted-foreground mb-4">
                All transactions are protected with 256-bit SSL encryption. We partner with Stripe, a PCI Level 1 certified payment processor, to help ensure your financial information is handled securely.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-muted-foreground"><CheckCircle2 className="w-4 h-4 text-green-500" /> Bank-level encryption</li>
                <li className="flex items-center gap-2 text-muted-foreground"><CheckCircle2 className="w-4 h-4 text-green-500" /> PCI DSS compliant payment processing</li>
                <li className="flex items-center gap-2 text-muted-foreground"><CheckCircle2 className="w-4 h-4 text-green-500" /> Fraud detection systems</li>
              </ul>
            </section>

            <section className="bg-card border border-border rounded-xl p-8">
              <div className="flex items-center gap-4 mb-4">
                <BadgeCheck className="w-8 h-8 text-teal-500" />
                <h2 className="text-xl font-semibold text-foreground">Campaign Verification</h2>
              </div>
              <p className="text-muted-foreground mb-4">
                We review campaigns to help ensure they meet our community guidelines. Campaigns that complete our verification process display a verification badge. Please note that verification indicates compliance with our guidelines but does not guarantee campaign legitimacy or outcomes.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-muted-foreground"><CheckCircle2 className="w-4 h-4 text-green-500" /> Identity verification (KYC)</li>
                <li className="flex items-center gap-2 text-muted-foreground"><CheckCircle2 className="w-4 h-4 text-green-500" /> Document review</li>
                <li className="flex items-center gap-2 text-muted-foreground"><CheckCircle2 className="w-4 h-4 text-green-500" /> Ongoing monitoring</li>
              </ul>
            </section>

            <section className="bg-card border border-border rounded-xl p-8">
              <div className="flex items-center gap-4 mb-4">
                <Users className="w-8 h-8 text-emerald-500" />
                <h2 className="text-xl font-semibold text-foreground">Donor Awareness</h2>
              </div>
              <p className="text-muted-foreground mb-4">
                We encourage donors to make informed decisions. While we work to maintain platform integrity, donors should understand that donations are made at their own risk. We cannot guarantee campaign outcomes or how funds will be used by campaign organizers.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-muted-foreground"><CheckCircle2 className="w-4 h-4 text-green-500" /> Review campaign details before donating</li>
                <li className="flex items-center gap-2 text-muted-foreground"><CheckCircle2 className="w-4 h-4 text-green-500" /> Look for verification badges</li>
                <li className="flex items-center gap-2 text-muted-foreground"><CheckCircle2 className="w-4 h-4 text-green-500" /> Report suspicious activity</li>
              </ul>
            </section>

            <section className="bg-card border border-border rounded-xl p-8">
              <div className="flex items-center gap-4 mb-4">
                <Scale className="w-8 h-8 text-blue-500" />
                <h2 className="text-xl font-semibold text-foreground">Platform Role</h2>
              </div>
              <p className="text-muted-foreground mb-4">
                FundTheHope operates as a technology platform connecting campaign organizers with donors. We do not endorse, guarantee, or verify the accuracy of campaigns. Campaign organizers are solely responsible for the truthfulness of their campaigns and for how they use funds received.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-muted-foreground"><CheckCircle2 className="w-4 h-4 text-green-500" /> We provide the technology platform</li>
                <li className="flex items-center gap-2 text-muted-foreground"><CheckCircle2 className="w-4 h-4 text-green-500" /> Campaign organizers are responsible for their campaigns</li>
                <li className="flex items-center gap-2 text-muted-foreground"><CheckCircle2 className="w-4 h-4 text-green-500" /> Donors donate at their own discretion</li>
              </ul>
            </section>

            <section className="bg-card border border-border rounded-xl p-8">
              <div className="flex items-center gap-4 mb-4">
                <Eye className="w-8 h-8 text-purple-500" />
                <h2 className="text-xl font-semibold text-foreground">Refund Policy</h2>
              </div>
              <p className="text-muted-foreground mb-4">
                Donations are generally non-refundable once processed. In cases of confirmed fraud or material policy violations, refunds may be considered on a case-by-case basis at FundTheHope's sole discretion. We do not guarantee refunds under any circumstances.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-muted-foreground"><CheckCircle2 className="w-4 h-4 text-green-500" /> Donations are voluntary contributions</li>
                <li className="flex items-center gap-2 text-muted-foreground"><CheckCircle2 className="w-4 h-4 text-green-500" /> Refunds are not guaranteed</li>
                <li className="flex items-center gap-2 text-muted-foreground"><CheckCircle2 className="w-4 h-4 text-green-500" /> Fraud cases reviewed individually</li>
              </ul>
            </section>

            <section className="bg-card border border-border rounded-xl p-8">
              <div className="flex items-center gap-4 mb-4">
                <AlertTriangle className="w-8 h-8 text-amber-500" />
                <h2 className="text-xl font-semibold text-foreground">Report Concerns</h2>
              </div>
              <p className="text-muted-foreground mb-4">
                If you see something suspicious, please report it immediately. Our Trust & Safety team reviews all reports and takes appropriate action based on our policies.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mt-4">
                <div className="flex-1 bg-secondary/50 rounded-lg p-4">
                  <p className="text-sm text-muted-foreground mb-1">Trust & Safety</p>
                  <a href="mailto:trust@fundthehope.com" className="text-emerald-500 hover:underline font-medium">trust@fundthehope.com</a>
                </div>
                <div className="flex-1 bg-secondary/50 rounded-lg p-4">
                  <p className="text-sm text-muted-foreground mb-1">Security Issues</p>
                  <a href="mailto:security@fundthehope.com" className="text-emerald-500 hover:underline font-medium">security@fundthehope.com</a>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default TrustPage;
